import {FormProvider, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {useTranslation} from "react-i18next";
import {Button, Grid} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {useRecoilState} from "recoil";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {Box} from "@mui/system";
import {
    ExcessGroupProductCodeLocationGroupDTO,
    ExcessGroupProductCodeLocationGroupEntityResponse,
    FormProductCodeLocationGroupExcessGroupAssociation,
    LocationGroupAssociation,
    ProductCodeLocationGroupAssociation,
} from "../../../api/types/excessGroupProductCodeLocationGroup";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {fromGenericBeToGenericFe, fromLocationGroupProductCodeBeToFe} from "../../../utils";
import {FormDialog, SelectElementModelFE, UseLocationStateModel} from "../../../api/types/generic";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {productCodesAtom} from "../../../state/locationGroupProductCodesRecoil";
import CardContainer from "../../../common/components/CardContainer";
import {useProductCodeRepository} from "../../../api/repositories/ProductCodeRepository";
import {FormAutocomplete} from "../../../common/form-fields";
import {useProductCodeLocationGroupRepository} from "../../../api/repositories/ProductCodeLocationGroupRepository";
import {useLocation, useNavigate} from "react-router-dom";
import {excessGroupSelectListAtom} from "../../../state/excessGroupsRecoil";
import {useExcessGroupRepository} from "../../../api/repositories/ExcessGroupRepository";
import {ExcessGroupSelectFE} from "../../../api/types/excessGroup";
import {
    useExcessGroupProductCodeLocationGroupRepository
} from "../../../api/repositories/ExcessGroupProductCodeLocationGroupRepository";
import {ProductCodeSelectFE} from "../../../api/types/productCode";
import Loading from "../../../common/components/Loading";
import {LocationGroupProductCodeModelFE} from "../../../api/types/locationGroupProductCode";
import {
    ExceptionListProductCodeLocationGroupEntityResponse
} from "../../../api/types/exceptionListProductCodeLocationGroup";
import FieldArray from "../../ExceptionListPlan/ExceptionProductCodeLocationGroupForm/fieldArray";
import DealerAndTenant from "../../../common/components/DealerAndTenant";
import {useExceptionListRepository} from "../../../api/repositories/ExceptionListRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const ExcessGroupProductCodeLocationGroupForm = ({
                                                     readOnly,
                                                 }: {
    readOnly: boolean;
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Navigate
    const navigate = useNavigate();

    const {globalData: {user}} = useContext(GlobalContext);

    // Use Location
    const location = useLocation();

    const locationState: UseLocationStateModel =
        location.state as UseLocationStateModel;

    //PER CHI ARRIVA DA SALES&MARKETING, NAVIGARE SU QUESTA PAGINA INSERENDO NELLO STATE DELLA PAGINA IL GRUPPO di FRANCHGIE
    //navigate("/pricing/product-code/location-group-excess-group/create", {replace: true, state: {excessGroupId: "1"}});

    // Use State
    const [formDialog] = useState<FormDialog | null>(null);
    const [initialValues] = useState<FormProductCodeLocationGroupExcessGroupAssociation>({
        commercialCategoryId: null,
        commercialSegmentId: null,
        excessGroup: null,
        associations: [],
        dealer: null,
        legalEntity: null,
    });
    const [oldAssociations, setOldAssociations] = useState<ExcessGroupProductCodeLocationGroupDTO[]>([]);
    const [locationGroupList, setLocationGroupList] = useState<{ [key: number]: LocationGroupProductCodeModelFE[] }>({});

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [productCodeList, setProductCodeList] = useRecoilState(productCodesAtom);
    const [excessGroupList, setExcessGroupList] = useRecoilState(excessGroupSelectListAtom);

    // Use Repository Hooks
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {
        getCommercialCategories,
        getCommercialCategoriesByCommercialSegmentId
    } = useCommercialCategoryRepository();
    const {getExcessGroup, getExcessGroupsByCommercialCategoryId} = useExcessGroupRepository();
    const {getLocationGroupsByProductCode} = useProductCodeLocationGroupRepository();
    const {getProductCodesByCommercialCategoryId} = useProductCodeRepository();
    const {
        getAssociations,
        postExcessGroupProductCodeLocationGroupAll
    } = useExcessGroupProductCodeLocationGroupRepository();

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    // ************ FORM - START ************ //
    const validationSchema = yup
        .object()
        .shape({
            dealer: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable(true),
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            commercialSegmentId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            commercialCategoryId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            excessGroup: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            associations: yup
                .array()
                .of(
                    yup.object().shape({
                        locationGroup: yup
                            .array()
                            .of(
                                yup.object().shape({
                                    code: yup.string(),
                                    label: yup.string(),
                                })
                            )
                            .min(1, t("pricing:validation.minOne"))
                            .nullable()
                            .required(t("translation:validation.fieldMandatory")),
                        productCode: yup
                            .array()
                            .of(
                                yup.object().shape({
                                    code: yup.string(),
                                    label: yup.string(),
                                })
                            )
                            .min(1, t("pricing:validation.minOne"))
                            .nullable()
                            .required(t("translation:validation.fieldMandatory")),
                    })
                )
                .nullable()
                .min(0),
        })
        .defined();

    // Use Form
    const form = useForm<FormProductCodeLocationGroupExcessGroupAssociation>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema
        ),
        shouldUnregister: true
    });
    const {
        control,
        register,
        getValues,
        formState: {errors},
        setValue
    } = form;
    const watchAllForm = form.watch();
    // ************ FORM - END ************ //

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    useEffect(() => {
        //atterraggio con gruppo di franchigie già selezionato
        const handleGetContent = async () => {
            if (form && locationState?.excessGroupId) {
                //ottenere gruppo di franchigie da id
                const exceptionListResponse = await getExcessGroup(locationState?.excessGroupId)
                setValue("legalEntity", {
                    code: exceptionListResponse.legalEntity,
                    label:  t('translation:legalEntities.'.concat(exceptionListResponse.legalEntity))
                })
                setValue("dealer", {
                    code: exceptionListResponse.dealer,
                    label:  t('translation:dealers.'.concat(exceptionListResponse.dealer))
                })
                const excessGroupEntityResponse = await getExcessGroup(
                    Number(locationState?.excessGroupId)
                );
                //ottenere lista segmenti commerciali
                // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
                // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
                const commercialSegmentsData = await getCommercialSegments();

                const commercialSegmentList: SelectElementModelFE[] =
                    fromGenericBeToGenericFe(commercialSegmentsData);
                setCommercialSegmentList(commercialSegmentList);

                //ottenere categoria commerciale specifica del gruppo di franchigie
                const commercialCategoryEntityResponse: CommercialCategoryEntityResponse =
                    excessGroupEntityResponse.commercialCategory;

                //selezionare segmento commerciale corrispondente a categoria commerciale in gruppo di franchigie
                const commercialSegment: SelectElementModelFE | undefined =
                    commercialSegmentList.find(
                        (cs) =>
                            cs.code === commercialCategoryEntityResponse.commercialSegment.id
                    );
                commercialSegment &&
                form.setValue("commercialSegmentId", commercialSegment);

                //ottenere lista di categorie commerciali del segmento commerciale
                const commercialCategoriesData =
                    await getCommercialCategoriesByCommercialSegmentId(
                        commercialSegment?.code as string
                    );
                const commercialCategoryList: CommercialCategorySelectFE[] =
                    fromGenericBeToGenericFe(commercialCategoriesData);
                setCommercialCategoryList(commercialCategoryList);

                //selezionare categoria commerciale di gruppo di franchigie
                const commercialCategory: CommercialCategorySelectFE | undefined =
                    commercialCategoryList.find(
                        (cc) => cc.code === commercialCategoryEntityResponse.id
                    );
                commercialCategory &&
                form.setValue("commercialCategoryId", commercialCategory);

                //ottenere lista di gruppi di franchigie da categoria commerciale
                const excessGroupsData = await getExcessGroupsByCommercialCategoryId(
                    commercialCategory?.code as string,
                        user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string || exceptionListResponse.dealer,
                    user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string || exceptionListResponse.legalEntity
                );
                const excessGroupList: ExcessGroupSelectFE[] =
                    fromGenericBeToGenericFe(excessGroupsData);
                setExcessGroupList(excessGroupList);

                const productCodeData = await getProductCodesByCommercialCategoryId(commercialCategory?.code as string, user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string || exceptionListResponse.dealer, user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string || exceptionListResponse.legalEntity);
                setProductCodeList([{
                    code: 0,
                    label: t("pricing:actions.selectAll")
                }, ...fromGenericBeToGenericFe(productCodeData).map(el => ({
                    code: el.code,
                    label: el.code + " - " + el.label
                })) as ProductCodeSelectFE[]]);

                //selezionare gruppo di franchigie
                const excessGroup: ExcessGroupSelectFE | undefined = excessGroupList.find(pc => pc.code === excessGroupEntityResponse.id);
                excessGroup && form.setValue("excessGroup", excessGroup);
            }
        };
        handleGetContent();
    }, [locationState?.excessGroupId]);

    useEffect(() => {
        const handleGetContent = async () => {
            const excessGroupId = watchAllForm?.excessGroup?.code as number;
            if (excessGroupId) {
                //get di associazioni già esistenti
                const associationsBe = await getAssociations(excessGroupId);

                const associations: ExcessGroupProductCodeLocationGroupEntityResponse[] =
                    associationsBe.filter(
                        (el: ExceptionListProductCodeLocationGroupEntityResponse) => {
                            return el.productCode?.length > 0;
                        }
                    );

                //get di locationGroups per ciascun prodotto nelle associazioni già esistenti
                let currentLocationGroupList: {
                    [key: number]: LocationGroupProductCodeModelFE[];
                } = {};
                for (const association of associations) {
                    const index: number = associations.indexOf(association);
                    let locationGroups: LocationGroupProductCodeModelFE[] = [];
                    for (let i = 0; i < association.productCode.length; i++) {
                        const locationGroupsData = await getLocationGroupsByProductCode(
                            association.productCode[i]
                        );

                        const currentLocationGroupList: LocationGroupProductCodeModelFE[] =
                            fromLocationGroupProductCodeBeToFe(
                                locationGroupsData,
                                association.productCode[i]
                            );
                        locationGroups = locationGroups.concat(currentLocationGroupList);
                    }
                    currentLocationGroupList = {
                        ...currentLocationGroupList,
                        [index]: locationGroups,
                    };
                }

                setLocationGroupList(currentLocationGroupList);

                const productCodeLocationGroupExcessGroupAssociation: ProductCodeLocationGroupAssociation[] =
                    associations.length > 0
                        ? associations.map((association) => {
                            const productCodes: ProductCodeSelectFE[] =
                                productCodeList.filter((pc) => {
                                    return association.productCode?.find((productCode) => {
                                        return productCode === pc.code;
                                    });
                                });

                            return {
                                locationGroup: association.associations?.map((lg: LocationGroupAssociation) => ({
                                    code: lg.id,
                                    label: lg.name,
                                    productCodeId: lg.prcProductCode
                                })),
                                productCode: productCodes || []
                            };
                        })
                        :
                        [{
                            locationGroup: [],
                            productCode: []
                        }];
                form.setValue("associations", productCodeLocationGroupExcessGroupAssociation);

                //creazione lista di associazioni già esistenti
                const oldAssociations: ExcessGroupProductCodeLocationGroupDTO[] = [];

                associations.forEach(
                    (association: ExcessGroupProductCodeLocationGroupEntityResponse) => {
                        association.associations.forEach(
                            (locationGroup: LocationGroupAssociation) => {
                                association.productCode.forEach((productCode: string) => {
                                    if (productCode === locationGroup.prcProductCode) {
                                        oldAssociations.push({
                                            excessGroupId,
                                            locationGroupId: locationGroup.id,
                                            productCodeId: productCode,
                                        });
                                    }
                                });
                            }
                        );
                    }
                );

                setOldAssociations(oldAssociations);

            }
        };
        handleGetContent();
    }, [watchAllForm?.excessGroup]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        if (value) {
            form.setValue("commercialSegmentId", value);
            const commercialCategoriesData =
                await getCommercialCategoriesByCommercialSegmentId(
                    (value as SelectElementModelFE).code as string
                );
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("commercialCategoryId", null);
            form.setValue("excessGroup", null);
            setCommercialCategoryList(commercialCategoryList);
        } else {
            form.setValue("commercialSegmentId", value);
            // TODO: togliere COMMERCIAL_CATEGORIES || quando si pusha
            // const commercialCategoriesData: CommercialCategoryEntityResponse[] = COMMERCIAL_CATEGORIES || await getCommercialCategories();
            const commercialCategoriesData: CommercialCategoryEntityResponse[] =
                await getCommercialCategories();

            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("commercialCategoryId", null);
            form.setValue("excessGroup", null);
            form.setValue("associations", []);

            setCommercialCategoryList(commercialCategoryList);
        }
    };

    const handleChangeCommercialCategory = async (
        value: CommercialCategorySelectFE
    ) => {
        if (value) {
            form.setValue("commercialCategoryId", value);
            const excessGroupsData = await getExcessGroupsByCommercialCategoryId(
                (value as CommercialCategorySelectFE).code as string,
                user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string,
                user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string
            );
            const excessGroupList: ExcessGroupSelectFE[] =
                fromGenericBeToGenericFe(excessGroupsData);
            form.setValue("excessGroup", null);
            setExcessGroupList(excessGroupList);

            const productCodesData = await getProductCodesByCommercialCategoryId((value as CommercialCategorySelectFE).code as string, user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string, user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string);
            setProductCodeList([{
                code: 0,
                label: t("pricing:actions.selectAll")
            }, ...fromGenericBeToGenericFe(productCodesData).map(el => ({
                code: el.code,
                label: el.code + " - " + el.label
            })) as ProductCodeSelectFE[]]);
        } else {
            form.setValue("commercialCategoryId", value);
            form.setValue("excessGroup", null);
            form.setValue("associations", []);

            setProductCodeList([]);
            setExcessGroupList([]);
        }
    };

    const handleChangeExcessGroup = async (value: SelectElementModelFE) => {
        form.setValue("excessGroup", value);
        if (!value) {
            form.setValue("associations", []);
            setOldAssociations([]);
        }
    };

    const handleChangeProductCode = async (
        value: ProductCodeSelectFE[],
        index: number
    ) => {
        let currentValue: ProductCodeSelectFE[];
        if (
            // è stato cliccato "selectAll
            value.find(
                (el: ProductCodeSelectFE) => el.label === t("pricing:actions.selectAll")
            ) &&
            // tra i vecchi valori non c'è "selectAll
            !getValues().associations[index].productCode?.find(
                (el: ProductCodeSelectFE) => el.label === t("pricing:actions.selectAll")
            )
        ) {
            // applica il "selectAll, verranno selezionati anche tutti i gruppi di location e sarà disabilitato l'input dei gruppi di location
            currentValue = productCodeList.filter(
                (el: ProductCodeSelectFE) => el.label !== t("pricing:actions.selectAll")
            );
        } else {
            // setta solo i prodotti selezionati
            currentValue = value.filter(
                (el: ProductCodeSelectFE) => el.label !== t("pricing:actions.selectAll")
            );
        }

        if (currentValue.length > 1) {
            let locationGroups: LocationGroupProductCodeModelFE[] = [];
            // get di tutti i location group associati ai prodotti selezionati
            for (let i = 0; i < currentValue.length; i++) {
                const locationGroupsData = await getLocationGroupsByProductCode(currentValue[i].code as string);
                const currentLocationGroupList: LocationGroupProductCodeModelFE[] = fromLocationGroupProductCodeBeToFe(locationGroupsData, currentValue[i].code as string);
                locationGroups = locationGroups.concat(currentLocationGroupList);
            }
            setLocationGroupList({...locationGroupList, [index]: locationGroups});
            const currentAssociations = [...getValues().associations];
            currentAssociations.splice(index, 1, {
                ...currentAssociations[index],
                productCode: currentValue,
                locationGroup: locationGroups
            });
            setValue("associations", currentAssociations);
        } else if (currentValue.length === 1) {
            // get di tutti i location group associati al prodotto selezionato

            let locationGroups: LocationGroupProductCodeModelFE[] = [];
            // get di tutti i location group associati ai prodotti selezionati
            for (let i = 0; i < currentValue.length; i++) {
                const locationGroupsData = await getLocationGroupsByProductCode(currentValue[i].code as string);
                const currentLocationGroupList: LocationGroupProductCodeModelFE[] = fromLocationGroupProductCodeBeToFe(locationGroupsData, currentValue[i].code as string);
                locationGroups = locationGroups.concat(currentLocationGroupList);
            }
            setLocationGroupList({
                ...locationGroupList, [index]: [{
                    code: 0,
                    label: t("pricing:actions.selectAll"),
                    productCodeId: ""
                }, ...locationGroups]
            });

            const currentAssociations = [...getValues().associations];
            currentAssociations.splice(index, 1, {
                ...currentAssociations[index],
                productCode: currentValue,
                locationGroup: locationGroups
            });
            setValue("associations", currentAssociations);
        } else {
            const currentAssociations = [...getValues().associations];
            currentAssociations.splice(index, 1, {
                ...currentAssociations[index],
                productCode: currentValue,
                locationGroup: []
            });
            setValue("associations", currentAssociations);
        }
    };

    const handleChangeLocationGroup = async (
        value: LocationGroupProductCodeModelFE[],
        index: number
    ) => {
        const currentValue: LocationGroupProductCodeModelFE[] =
            // è stato cliccato "selectAll
            value.find(
                (el: LocationGroupProductCodeModelFE) =>
                    el.label === t("pricing:actions.selectAll")
            ) &&
            // tra i vecchi valori non c'è "selectAll
            !getValues().associations[index].locationGroup?.find(
                (el: LocationGroupProductCodeModelFE) =>
                    el.label === t("pricing:actions.selectAll")
            )
                ? // applica il "selectAll
                locationGroupList[index].filter(
                    (el: LocationGroupProductCodeModelFE) =>
                        el.label !== t("pricing:actions.selectAll")
                )
                : // setta solo i car group selezionati
                value.filter(
                    (el: LocationGroupProductCodeModelFE) =>
                        el.label !== t("pricing:actions.selectAll")
                );

        const currentAssociations = [...getValues().associations];
        currentAssociations.splice(index, 1, {
            ...currentAssociations[index],
            locationGroup: currentValue
        });
        setValue("associations", currentAssociations);
    };

    const handleConfirm = async () => {
        const excessGroupId = getValues()?.excessGroup?.code as number;
        const currentAssociationsArray: ExcessGroupProductCodeLocationGroupDTO[] = [];

        //costruire array associazioni
        getValues().associations.forEach(association => {
            association.locationGroup.forEach((lg: LocationGroupProductCodeModelFE) => {
                association.productCode.forEach((productCode: SelectElementModelFE) => {
                    if (productCode.code === lg.productCodeId) {
                        const currentAssociation: ExcessGroupProductCodeLocationGroupDTO = {
                            excessGroupId,
                            locationGroupId: Number(lg.code),
                            productCodeId: String(productCode.code)
                        };
                        currentAssociationsArray.push(currentAssociation);
                    }
                });
            });
        });

        // let difference = arr1.filter(x => !arr2.includes(x));
        // includes === arr2.filter(e => e.locationGroupId === x.locationGroupId).length > 0

        //current - old => associazioni da inserire
        const associazioniDaInserire: ExcessGroupProductCodeLocationGroupDTO[] =
            currentAssociationsArray.filter((ca) => {
                return !(
                    oldAssociations.filter((oa) => {
                        return (
                            oa.excessGroupId === ca.excessGroupId &&
                            oa.locationGroupId === ca.locationGroupId &&
                            ca.productCodeId === oa.productCodeId
                        );
                    })?.length > 0
                );
            });
        //old - current => associazioni da togliere
        const associazioniDaEliminare: ExcessGroupProductCodeLocationGroupDTO[] =
            oldAssociations.filter((oa) => {
                return !(
                    currentAssociationsArray.filter((ca) => {
                        return (
                            ca.excessGroupId === oa.excessGroupId &&
                            ca.locationGroupId === oa.locationGroupId &&
                            ca.productCodeId === oa.productCodeId
                        );
                    })?.length > 0
                );
            });

        await postExcessGroupProductCodeLocationGroupAll({
            associationsToSave: associazioniDaInserire,
            associationsToDelete: associazioniDaEliminare,
        });
        navigate("../product-code/location-group-excess-group/list");
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_EXCESS_GROUP_PLAN_CREATE.name")}/>

            <FormProvider {...form}>
                <CardContainer>
                    <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                        <DealerAndTenant
                            readOnly={readOnly}
                            md={3}
                            legalEntityName={"legalEntity"}
                            dealerName={"dealer"}
                            dealerIsVisible={true}
                        />
                        {/* COMMERCIAL SEGMENTS */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("commercialSegmentId")}
                                label={`${t("pricing:excessGroupProductCodeLocationGroup.form.labels.commercialSegment")}*`}
                                options={commercialSegmentList}
                                isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                readOnly={Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeCommercialSegment}
                            />
                        </Grid>

                        {/* COMMERCIAL CATEGORY */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("commercialCategoryId")}
                                label={`${t("pricing:excessGroupProductCodeLocationGroup.form.labels.commercialCategory")}*`}
                                options={commercialCategoryList}
                                isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                readOnly={!watchAllForm.commercialSegmentId || Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeCommercialCategory}
                            />
                        </Grid>

                        {/* PRODUCT CODE */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("excessGroup")}
                                label={`${t("pricing:excessGroupProductCodeLocationGroup.form.labels.excessGroup")}*`}
                                options={excessGroupList}
                                isOptionEqualToValue={(option: ExcessGroupSelectFE, value: ExcessGroupSelectFE) => option?.code === value?.code}
                                readOnly={!watchAllForm.commercialCategoryId || Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeExcessGroup}
                            />
                        </Grid>
                    </Grid>
                </CardContainer>

                <FieldArray
                    {...{
                        control,
                        register,
                        defaultValues: initialValues,
                        getValues,
                        setValue,
                        errors,
                        formDialog,
                        handleChangeProductCode,
                        locationGroupList,
                        handleChangeLocationGroup,
                        label: getValues()?.excessGroup?.label || ""
                    }}
                />

                {!Boolean(formDialog?.readonly) &&
                    <Grid container item xs={12} justifyContent="flex-end" columnGap={2} p={2}>
                        <Grid>
                            <Button variant="outlined"
                                    sx={{marginRight: "15px"}}
                                    color={"accent"}
                                    type={"button"}
                                    disabled={form.formState.isSubmitting}
                                    onClick={() => {
                                        navigate("../product-code/location-group-excess-group/list");
                                    }}
                            >
                                {t("pricing:actions.cancel")}
                            </Button>
                            <Button color={"accent"}
                                    type={"button"}
                                    onClick={form.handleSubmit(handleConfirm)}
                                    disabled={form.formState.isSubmitting}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </Grid>
                    </Grid>
                }
            </FormProvider>

            <Loading/>
        </Box>
    );
};

export default ExcessGroupProductCodeLocationGroupForm;
